import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  IconAiStar,
  IconAiPencil,
  IconAiPencil2,
  IconDownload,
  IconEdit,
  IconHome2,
  Modal,
  SelectField,
  TextField,
  IconLoading,
  IconImageError,
} from 'gatorcomponents';
import { IconAiPencil as IconAiPencilGradient } from '../Icons/IconAiPencil';
import * as Styles from './ModalLogoGeneration.styles';
import useLocale from '@/hooks/useLocale';
import { useDispatch, useSelector } from 'react-redux';
import { studioActions } from '@/redux/modules';
import { useResize } from '@/hooks/useResize';
import GradientButton from '../GradientButton';
import { useStudio } from '@/hooks/studio';
import ColorCards from './ColorCards/ColorCards';
import AIStarLoading from './AIStarLoading';
import { studioAnalytics } from '@/analytics/studio';

const initialFormValues = {
  brandName: '',
  brandSlogan: '',
  category: '',
  color: '',
  customColor: '',
  hasError: false,
};

const ModalLogoGeneration = ({ isProPlan, selectedPaidPlan, handleCloseModalLogoGeneration }) => {
  const { studio: studioLocale } = useLocale();
  const { openStudio } = useStudio();
  const { modalLogoGeneration: modalLogoGenerationLocale } = studioLocale;
  const [formValues, setFormValues] = useState(initialFormValues);
  const [shouldDisableButton, setShouldDisableButton] = useState(true);
  const [showCustomColor, setShowCustomColor] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [currentLogoGeneration, setCurrentLogoGeneration] = useState(0);
  const dispatch = useDispatch();
  const {
    logoBuilderLoading, listLogos, logoBuilder, errorGeneratingLogo, logoDownloadLoading,
  } = useSelector(state => state.studio);
  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;
  const categoryOptions = modalLogoGenerationLocale.categories.map(option => ({ label: option, value: option }));
  const windowRect = useResize();
  const { width } = windowRect;
  const mobile = width < 960;

  const setNewFormValue = (field, value) => {
    setFormValues({ ...formValues, [field]: value });
  };

  const validateAndGoNextStep = () => {
    studioAnalytics.createLogoClick();

    if (!formValues.brandName || !formValues.brandSlogan || !formValues.category || !formValues.color) {
      setFormValues({ ...formValues, hasError: true });
    }

    if (formValues.brandName && formValues.brandSlogan && formValues.category && formValues.color) {
      const payload = {
        brand_name: formValues.brandName,
        slogan: formValues.brandSlogan || 'Sem slogan',
        business_category: formValues.category,
        brand_color: formValues.color,
        ...(formValues.customColor && { secondary_color: formValues.customColor }),
      };

      dispatch(studioActions.logoGeneration.request(payload));
      setCurrentStep(currentStep + 1);
      setCurrentLogoGeneration(currentLogoGeneration + 1);
    }
  };

  const handleSetShouldDisableButton = useCallback(() => {
    setShouldDisableButton(true);

    if (formValues.brandName && formValues.brandSlogan && formValues.category && formValues.color) {
      setShouldDisableButton(false);
    }
  }, [formValues]);

  const handleCloseModal = (backButtonClick) => {
    handleCloseModalLogoGeneration(false);
    setFormValues(initialFormValues);
    setCurrentStep(1);
    dispatch(studioActions.logoGeneration.resetStatus());

    backButtonClick ? studioAnalytics.modalLogoButtonBackClick() : studioAnalytics.goToStudioClick();
  };

  const handleCustomColor = () => {
    setShowCustomColor(true);
  };

  const formErrorRevalidation = useCallback(() => {
    if (formValues.brandName && formValues.category && formValues.color && formValues.hasError) {
      setFormValues({ ...formValues, hasError: false });
    }
  }, [formValues]);

  const handleGenerateMore = () => {
    studioAnalytics.generateMoreOptionsClick();

    const payload = {
      brand_name: formValues.brandName,
      slogan: formValues.brandSlogan || 'Sem slogan',
      business_category: formValues.category,
      brand_color: formValues.color,
      ...(formValues.customColor && { secondary_color: formValues.customColor }),
    };

    dispatch(studioActions.logoGeneration.request(payload));
    setCurrentLogoGeneration(currentLogoGeneration + 1);
  };

  const getTitleLogosCreated = () => {
    const logoGenerated = currentLogoGeneration * 3;
    const text = modalLogoGenerationLocale.titleLogosCreated.replace('%number%', logoGenerated);

    return text;
  };

  const handleFormError = useCallback(() => {
    errorGeneratingLogo && setCurrentStep(3);
  }, [errorGeneratingLogo]);

  const handleDownloadLogo = useCallback((id) => {
    studioAnalytics.downloadLogoClick();
    dispatch(studioActions.logoDownload.request(id));
  }, [dispatch]);

  const handleEditLogo = useCallback((id) => {
    studioAnalytics.editLogoClick();
    openStudio('edit_document', id);
  }, [openStudio]);

  const handleCreateLogoManually = useCallback(() => {
    studioAnalytics.createLogoManuallyClick();
    openStudio('logo_builder');
  }, [openStudio]);

  useEffect(handleFormError, [handleFormError]);

  useEffect(() => {
    formErrorRevalidation();
  }, [formErrorRevalidation]);

  useEffect(() => {
    handleSetShouldDisableButton();
  }, [handleSetShouldDisableButton]);

  const ModalTitle = () => (
    <Styles.ModalTitleContainer isProPlan={isProPlan}>
      <Styles.Column align="flex-start" type="studioTitle">
        <Styles.WrapperStudioTitle>
          <Styles.StudioTitle>
            {isProPlan
              ? studioLocale.studioTitle.pro
              : studioLocale.studioTitle.free
            }

          </Styles.StudioTitle>
          <IconAiPencil2
            color="highPure"
            size={mobile ? 'sm' : 'md'}
            testId="myIconTestId"
          />
        </Styles.WrapperStudioTitle>
      </Styles.Column>

      <Styles.Column align="center" type="logoBuilder">
        <Styles.ModalTitle>
          {studioLocale.logoBuilder}
        </Styles.ModalTitle>
      </Styles.Column>

      <Styles.Column align="flex-end" type="button">
        {mobile ? (
          <Styles.WrapperButtonGoStudio>
            <Button
              label=""
              onClick={() => handleCloseModal(false)}
              variant="secondary"
              iconLeft={<IconHome2 color="highPure" size="sm" />}
              inverse
              size="small"
              version="v2"
            />
          </Styles.WrapperButtonGoStudio>
        ) : (
          <Button
            label={isProPlan
              ? modalLogoGenerationLocale.buttonGoStudio.labelPro
              : modalLogoGenerationLocale.buttonGoStudio.labelFree
              }
            onClick={() => handleCloseModal(false)}
            variant="secondary"
            inverse
            size="small"
            version="v2"
            testId="buttonGoStudio"
          />
        )}
      </Styles.Column>
    </Styles.ModalTitleContainer>
  );

  return (
    <Modal
      maxHeight={viewportHeight}
      maxWidth={viewportWidth}
      noContentPadding
    >
      <Styles.ModalContainer>
        {logoBuilderLoading && !listLogos.length && (
          <Styles.ModalStep step="step-loading">
            <ModalTitle />

            <Styles.Content>
              <Styles.WrapperTitle step="step-loading">
                <Styles.LogoBuilderTitle isProPlan={isProPlan}>
                  {modalLogoGenerationLocale.titleCreatingLogos}
                </Styles.LogoBuilderTitle>
              </Styles.WrapperTitle>

              <Styles.WrapperListLogos>
                <Styles.WrapperLogo isLoading="true"><Styles.LogoLoading><AIStarLoading /></Styles.LogoLoading></Styles.WrapperLogo>
                <Styles.WrapperLogo isLoading="true"><Styles.LogoLoading><AIStarLoading /></Styles.LogoLoading></Styles.WrapperLogo>
                <Styles.WrapperLogo isLoading="true"><Styles.LogoLoading><AIStarLoading /></Styles.LogoLoading></Styles.WrapperLogo>
              </Styles.WrapperListLogos>
            </Styles.Content>
          </Styles.ModalStep>
        )}

        {!logoBuilderLoading && currentStep === 1 && (
          <Styles.ModalStep step={`step-${currentStep}`}>
            <ModalTitle />

            <Styles.Content>
              <Styles.WrapperTitle step={`step-${currentStep}`}>
                <Styles.LogoBuilderTitle isProPlan={isProPlan}>
                  {modalLogoGenerationLocale.createYourLogoInSeconds}
                </Styles.LogoBuilderTitle>

                <Styles.ModalSubtitle>
                  {modalLogoGenerationLocale.tellUsAboutYourBrand}
                </Styles.ModalSubtitle>
              </Styles.WrapperTitle>

              {selectedPaidPlan && (
              <Styles.ModalDescription>
                {modalLogoGenerationLocale.itIsATest}
              </Styles.ModalDescription>
              )}

              {selectedPaidPlan && (
              <Styles.TellUsTitle>
                {modalLogoGenerationLocale.tellUsAboutYourBrand}
              </Styles.TellUsTitle>
              )}

              <Styles.Form>
                <TextField
                  name="brandName"
                  label={modalLogoGenerationLocale.brandName}
                  onChange={event => setNewFormValue('brandName', event.target.value)}
                  value={formValues.brandName}
                  status={formValues.hasError ? 'error' : 'normal'}
                  testId="brandName"
                  maxLength={100}
                />

                <TextField
                  name="brandSlogan"
                  label={modalLogoGenerationLocale.brandSlogan}
                  onChange={event => setNewFormValue('brandSlogan', event.target.value)}
                  value={formValues.brandSlogan}
                  helpText={modalLogoGenerationLocale.helpTextSlogan}
                  testId="brandSlogan"
                  maxLength={100}
                />

                <SelectField
                  placeholder={modalLogoGenerationLocale.category}
                  withScroll
                  options={categoryOptions}
                  onClickSelect={option => setNewFormValue('category', option.value)}
                  value={formValues.category}
                  error={formValues.hasError}
                  testId="category"
                />
              </Styles.Form>

              <Styles.ChooseColor>
                {modalLogoGenerationLocale.selectColor}
              </Styles.ChooseColor>

              <ColorCards brandColor="color" formValues={formValues} setNewFormValue={setNewFormValue} />

              {formValues.hasError && (
              <Styles.ErrorMessage>
                {modalLogoGenerationLocale.errorMessage}
              </Styles.ErrorMessage>
              )}

              {!showCustomColor && (
                <Styles.CustomColor>
                  <Button
                    label="Adicionar cor secundária"
                    onClick={() => handleCustomColor()}
                    variant="tertiary"
                    size="large"
                    testId="buttonCustomColor"
                    version="v2"
                  />
                </Styles.CustomColor>
              )}

              {showCustomColor && (
                <>
                  <Styles.ChooseColor>
                    {modalLogoGenerationLocale.selectCustomColor}
                  </Styles.ChooseColor>

                  <ColorCards brandColor="customColor" formValues={formValues} setNewFormValue={setNewFormValue} />
                </>
              )}

              <Styles.Actions>
                <Button
                  label={modalLogoGenerationLocale.buttonBackLabel}
                  onClick={() => handleCloseModal(true)}
                  variant="tertiary"
                  size="large"
                  version="v2"
                />
                <Button
                  label={modalLogoGenerationLocale.buttonCreateLabel}
                  onClick={validateAndGoNextStep}
                  size="large"
                  version="v2"
                  testId="buttonCreate"
                  disabled={shouldDisableButton || formValues.hasError}
                />
              </Styles.Actions>
            </Styles.Content>
          </Styles.ModalStep>
        )}

        {currentStep === 2 && listLogos.length && (
        <Styles.ModalStep step="step-2">
          <ModalTitle />

          <Styles.Content>
            <Styles.WrapperTitle step={`step-${currentStep}`}>
              {isProPlan ? (
                <IconAiPencilGradient size="large" />
              ) : (
                <IconAiPencil2 color="primaryBlue" size="lg" />
              )}

              <Styles.LogoBuilderTitle isProPlan={isProPlan}>
                {logoBuilderLoading
                  ? modalLogoGenerationLocale.titleCreatingLogos
                  : getTitleLogosCreated()
                }
              </Styles.LogoBuilderTitle>
            </Styles.WrapperTitle>

            {listLogos.length && (
              <Styles.WrapperListLogos>
                {listLogos.map(logo => (
                  <Styles.WrapperLogo key={`logo-${logo.id}`}>
                    <Styles.WrapperImage dangerouslySetInnerHTML={{ __html: logo.svg }} />

                    <Styles.LogoActions>
                      <Button
                        label=""
                        onClick={() => handleDownloadLogo(logo.id)}
                        size="large"
                        iconLeft={logoDownloadLoading.includes(logo.id) ? <IconLoading /> : <IconDownload color="highLight" />}
                        title="Download"
                      />

                      <Button
                        label=""
                        onClick={() => handleEditLogo(logo.id)}
                        size="large"
                        variant="secondary"
                        iconLeft={<IconEdit color="primaryMedium" />}
                        title="Editar"
                      />
                    </Styles.LogoActions>
                  </Styles.WrapperLogo>
                ))}

                {logoBuilderLoading && (
                  <>
                    <Styles.WrapperLogo isLoading="true"><Styles.LogoLoading><AIStarLoading /></Styles.LogoLoading></Styles.WrapperLogo>
                    <Styles.WrapperLogo isLoading="true"><Styles.LogoLoading><AIStarLoading /></Styles.LogoLoading></Styles.WrapperLogo>
                    <Styles.WrapperLogo isLoading="true"><Styles.LogoLoading><AIStarLoading /></Styles.LogoLoading></Styles.WrapperLogo>
                  </>
                )}
              </Styles.WrapperListLogos>
            )}

            {!logoBuilderLoading && logoBuilder.retry && (
              <>
                <Styles.BoxRetry>
                  <Button
                    label={modalLogoGenerationLocale.buttonRetryLabel}
                    onClick={() => handleGenerateMore()}
                    variant="secondary"
                    size="large"
                    version="v2"
                  />

                  <Styles.WrapperTextRetry>
                    <IconAiStar color="primaryMedium" size="md" />
                    <Styles.TextRetry>
                      {modalLogoGenerationLocale.textRetry}
                    </Styles.TextRetry>
                  </Styles.WrapperTextRetry>
                </Styles.BoxRetry>
              </>
            )}

            {!logoBuilderLoading && !logoBuilder.retry && (
              <Styles.BoxNoCredits>
                <Styles.IconBoxNoCredits>
                  <IconAiPencil color="highLight" />
                </Styles.IconBoxNoCredits>

                <Styles.TextBoxNoCredits>
                  {listLogos.length && logoBuilder.available === 0
                    ? modalLogoGenerationLocale.noCredits
                    : modalLogoGenerationLocale.noCreditsForLogoBuilder
                  }
                </Styles.TextBoxNoCredits>

                <Styles.ActionBoxNoCredits>
                  <Button
                    label={modalLogoGenerationLocale.buttonCreateLogoManuallyLabel}
                    onClick={() => handleCreateLogoManually()}
                    variant="secondary"
                    size="large"
                    version="v2"
                    width={!isProPlan ? 'full' : 'fit-content'}
                  />

                  {!isProPlan && (
                    <GradientButton
                      testId="upgrade-to-pro-button"
                      label={modalLogoGenerationLocale.buttonGoProLabel}
                      onClick={() => {}}
                      variant="primary"
                      width="full"
                    />
                  )}
                </Styles.ActionBoxNoCredits>
              </Styles.BoxNoCredits>
            )}
          </Styles.Content>
        </Styles.ModalStep>
        )}

        {currentStep === 3 && (
          <Styles.ModalStep step="step-3">
            <ModalTitle />

            <Styles.Content step="step-3">
              <Styles.TitleError>
                <IconImageError
                  color="primaryExtraDark"
                  secohndaryColor="primaryLight"
                  size="xl"
                  testId="IconImageError"
                />
                {modalLogoGenerationLocale.titleError}
              </Styles.TitleError>
              <Button
                label={modalLogoGenerationLocale.buttonErrorLabel}
                onClick={validateAndGoNextStep}
                size="large"
                version="v2"
                testId="buttonTryAgain"
              />
            </Styles.Content>
          </Styles.ModalStep>
        )}
      </Styles.ModalContainer>
    </Modal>
  );
};

export default ModalLogoGeneration;
